import { track } from '@amplitude/analytics-browser';
import { createFileRoute, redirect } from '@tanstack/react-router';
import dayjs from 'dayjs';

import { userAtom } from '@/entities/auth/atoms/user-atom';
import { fetchUser } from '@/entities/auth/fetch-user';
import { USER_ROLE } from '@/entities/user/user-constants';
import { buildWorkspaceQueryOptions } from '@/entities/workspace/query';
import { WorkspaceLayoutRoute } from '@/features/workspace/ui';
import { queryClient } from '@/shared/query-client';
import { buildLoginSearch } from '@/shared/redirect/build-login-search';
import { store } from '@/shared/store/store';
import z from 'zod';

const ALLOWED_PATHS = ['invalid-workspace', 'checkout', 'settings/plan', 'plan-required'];

const searchParams = z.object({
  productId: z.string().optional(),
  priceId: z.string().optional(),
});

export const Route = createFileRoute('/workspace/$workspaceId')({
  validateSearch: searchParams,
  loader: {
    staleReloadMode: 'blocking',
    handler: async ({ params, location }) => {
      const { workspaceId } = params;
      if (workspaceId === 'internal') {
        return;
      }

      if (!store.get(userAtom)) {
        try {
          const user = await fetchUser();
          store.set(userAtom, user);
        } catch {
          throw redirect({
            search: buildLoginSearch(location.searchStr),
            to: '/login',
          });
        }
      }

      const workspace = await queryClient.ensureQueryData(buildWorkspaceQueryOptions(workspaceId));

      const isAllowedPath = ALLOWED_PATHS.some(
        (path) => location.pathname === `/workspace/${workspaceId}/${path}`,
      );

      const isExpired = dayjs.unix(workspace.plan_expired_at).isBefore(dayjs());
      const isOwner = workspace.privilege === USER_ROLE.OWNER;
      if (isExpired && !isAllowedPath) {
        if (isOwner) {
          throw redirect({
            to: '/workspace/$workspaceId/plan-required',
            params,
          });
        } else {
          throw redirect({
            to: '/workspace/$workspaceId/invalid-workspace',
            params,
          });
        }
      }

      const workspacePath = location.pathname.split(workspaceId)[1];

      track('PageView', {
        path: workspacePath,
        workspaceId: workspaceId,
      });
    },
  },
  shouldReload: true,
  component: WorkspaceLayoutRoute,
});
